import {
  OLD_LOCAL_STORAGE_VERIFICATION,
  SESSION_STORAGE_DELETION_CANCELLED,
  SESSION_STORAGE_SMART_INVITE_SHARE_LINK,
  SESSION_STORAGE_VERIFICATION,
  VerificationTypeV2,
} from '@seaters/constants';
import { useSyncExternalStore } from 'react';
import { getVerificationToken } from '../utils';

export function getSessionData(): VerificationTypeV2 | null {
  let sessionData;

  const sessionStorageData = sessionStorage.getItem(
    SESSION_STORAGE_VERIFICATION
  );

  if (sessionStorageData) {
    sessionData = JSON.parse(sessionStorageData);
  }

  const localStorageData = localStorage.getItem(OLD_LOCAL_STORAGE_VERIFICATION);

  if (localStorageData) {
    sessionData = JSON.parse(localStorageData);
  }

  return sessionData;
}

type SessionDataListener = () => void;

const sessionDataListeners = new Set<SessionDataListener>();

/**
 * Subscribes to session establishment/teardown. Storage on its own cannot be
 * observed inside the tab that wrote it, so every mutation goes through
 * `setSessionData`/`clearSessionData` and notifies here.
 */
export function subscribeToSessionData(listener: SessionDataListener) {
  sessionDataListeners.add(listener);

  return () => {
    sessionDataListeners.delete(listener);
  };
}

function notifySessionDataChanged() {
  sessionDataListeners.forEach((listener) => listener());
}

export function setSessionData(data: VerificationTypeV2) {
  if (data) {
    sessionStorage.setItem(SESSION_STORAGE_VERIFICATION, JSON.stringify(data));
    if (data.scheduledDeletionCancelled) {
      sessionStorage.setItem(SESSION_STORAGE_DELETION_CANCELLED, 'true');
    }
  } else {
    sessionStorage.removeItem(SESSION_STORAGE_VERIFICATION);
  }

  notifySessionDataChanged();
}

/**
 * Drops the session from both the current (session) and legacy (local) stores.
 * The legacy entry has to go too, because `getSessionData` prefers it — leaving
 * it behind would keep resurrecting a dead session.
 */
export function clearSessionData() {
  sessionStorage.removeItem(SESSION_STORAGE_VERIFICATION);
  localStorage.removeItem(OLD_LOCAL_STORAGE_VERIFICATION);
  // The announce-once flag belongs to the session that set it. The 401 interceptor drops the
  // session through here without passing useLogout, and a survivor would announce a cancellation
  // that this sign-in never made.
  sessionStorage.removeItem(SESSION_STORAGE_DELETION_CANCELLED);
  // Session cleanup: a remembered share link belongs to the session that opened it.
  sessionStorage.removeItem(SESSION_STORAGE_SMART_INVITE_SHARE_LINK);

  notifySessionDataChanged();
}

export function getSessionToken(): string | null {
  const sessionData = getSessionData();

  if (!sessionData) return null;

  const token = getVerificationToken(sessionData);

  return token;
}

/**
 * Reactive form of `getSessionToken`.
 *
 * A bare `getSessionToken()` in a component body is a one-shot read: a component
 * that renders before the session is written keeps its stale `null`. Route
 * guards did that and bounced authenticated users into a redirect loop
 * (SEATERS-APP-16). Use this wherever rendered output depends on being signed in.
 */
export function useSessionToken(): string | null {
  return useSyncExternalStore(
    subscribeToSessionData,
    getSessionToken,
    // The server/prerender snapshot: no browser storage exists, so no session.
    () => null
  );
}

/**
 * Whether the last sign-in cancelled a pending account deletion and the fan has not been told yet.
 * Subscribes to the same notifier as the session itself, so the modal appears on the screen the
 * fan lands on rather than the sign-in form that navigated away.
 */
export function useScheduledDeletionCancelled(): boolean {
  return useSyncExternalStore(
    subscribeToSessionData,
    () => sessionStorage.getItem(SESSION_STORAGE_DELETION_CANCELLED) === 'true',
    () => false
  );
}

/** Clears the flag so the cancellation is announced exactly once. */
export function acknowledgeScheduledDeletionCancelled() {
  sessionStorage.removeItem(SESSION_STORAGE_DELETION_CANCELLED);
  notifySessionDataChanged();
}
